/**
 * UpdatePasswordPage — Phase 45 (Password Recovery Flow)
 *
 * Reached when a user clicks the "Reset password" link in their email.
 * Supabase fires PASSWORD_RECOVERY in onAuthStateChange, which sets a
 * flag in AuthContext; App.tsx then navigates here.
 *
 * Flow:
 *   1. User clicks email link → browser lands on / with #access_token in the
 *      URL fragment (Supabase PKCE / implicit).
 *   2. AuthContext listener fires event = 'PASSWORD_RECOVERY', sets
 *      isPasswordRecovery = true and navigates to /update-password.
 *   3. This page lets the user enter + confirm a new password.
 *   4. On success: calls supabase.auth.updateUser({ password }) → then
 *      signs out the recovery session and redirects to /login so the user
 *      logs in freshly with the new password.
 *
 * Design:
 *   • Public route — no ProtectedRoute wrapper (the recovery session IS the
 *     auth, we don't want ProtectedRoute sending them to dashboard).
 *   • If the user lands here without a recovery session they are redirected
 *     to / immediately.
 */

import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function StrengthBar({ password }: { password: string }) {
  const score =
    (password.length >= 8 ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/[0-9]/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);

  const labels = ['', 'חלשה', 'בינונית', 'טובה', 'חזקה'];
  const colours = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'];

  if (!password) return null;

  return (
    <div className="space-y-1 mt-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300
              ${i <= score ? colours[score] : 'bg-gray-200'}`}
          />
        ))}
      </div>
      <p className={`text-xs font-medium ${
        score <= 1 ? 'text-red-500' :
        score === 2 ? 'text-amber-500' :
        score === 3 ? 'text-blue-500' : 'text-green-600'
      }`}>
        {score > 0 && `סיסמה ${labels[score]}`}
      </p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UpdatePasswordPage() {
  const navigate   = useNavigate();
  const { session, isPasswordRecovery } = useAuth();

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [showCf,    setShowCf]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  // Guard: if there is no active recovery session, redirect home.
  // We check both isPasswordRecovery (set by listener) and session existence.
  // Give the listener 800 ms to fire before bailing out.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!session && !isPasswordRecovery) {
        navigate('/', { replace: true });
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [session, isPasswordRecovery, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('הסיסמה חייבת להכיל לפחות 8 תווים');
      return;
    }
    if (password !== confirm) {
      setError('הסיסמאות אינן תואמות');
      return;
    }

    setLoading(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;

      setSucceeded(true);

      // Sign out the recovery session so user must log in fresh.
      // Small delay so the success message is visible.
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login', {
          replace: true,
          state: { message: 'הסיסמה עודכנה. נא להתחבר עם הסיסמה החדשה.' },
        });
      }, 2000);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'שגיאה בעדכון הסיסמה — נסה שוב');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (succeeded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                      flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center space-y-4">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900">הסיסמה עודכנה!</h2>
          <p className="text-sm text-gray-500">מעביר אותך לדף הכניסה…</p>
          <Loader2 className="w-5 h-5 animate-spin text-primary-500 mx-auto" />
        </div>
      </div>
    );
  }

  // ── Loading guard (waiting for auth listener) ──────────────────────────────
  if (!session && !isPasswordRecovery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                 flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="w-full max-w-md">
        {/* Logo / header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl
                          bg-primary-600 shadow-lg mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">עדכון סיסמה</h1>
          <p className="text-gray-500 mt-1 text-sm">הזן סיסמה חדשה לחשבונך</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-5">

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border
                            border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>

            {/* New password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                סיסמה חדשה
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                                 text-gray-400 pointer-events-none" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="לפחות 8 תווים"
                  required
                  dir="ltr"
                  className="w-full pr-9 pl-10 py-2.5 border border-gray-300 rounded-xl
                             text-sm focus:outline-none focus:ring-2 focus:ring-primary-400
                             focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <StrengthBar password={password} />
            </div>

            {/* Confirm password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                אימות סיסמה
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                                 text-gray-400 pointer-events-none" />
                <input
                  type={showCf ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="הזן שוב את הסיסמה החדשה"
                  required
                  dir="ltr"
                  className={`w-full pr-9 pl-10 py-2.5 border rounded-xl text-sm
                             focus:outline-none focus:ring-2 focus:ring-primary-400
                             focus:border-transparent transition-colors
                             ${confirm && confirm !== password
                               ? 'border-red-300 bg-red-50'
                               : confirm && confirm === password
                               ? 'border-green-300 bg-green-50'
                               : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowCf(v => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400
                             hover:text-gray-600 transition-colors"
                >
                  {showCf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirm && confirm === password && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> הסיסמאות תואמות
                </p>
              )}
              {confirm && confirm !== password && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> הסיסמאות אינן תואמות
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !password || !confirm}
              className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700
                         text-white font-semibold text-sm transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2 shadow-sm"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> מעדכן…</>
                : 'עדכן סיסמה'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
